import { useCallback, useEffect, useMemo, useState } from 'react'

export type ScriptAIOutputKind = 'generate' | 'probe'

export type ScriptAIOutputRecord = {
  id: string
  kind: ScriptAIOutputKind
  text: string
  label: string
  createdAt: number
  cost?: number
  prompt?: string
  modelName?: string
}

const MAX_ITEMS = 80

function loadHistory(key: string): ScriptAIOutputRecord[] {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw) as ScriptAIOutputRecord[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveHistory(key: string, items: ScriptAIOutputRecord[]) {
  try {
    localStorage.setItem(key, JSON.stringify(items.slice(0, MAX_ITEMS)))
  } catch {
    /* quota */
  }
}

function newId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function useScriptAIHistory(storageKey: string) {
  const [items, setItems] = useState<ScriptAIOutputRecord[]>(() => loadHistory(storageKey))
  const [activeId, setActiveId] = useState<string | null>(() => loadHistory(storageKey)[0]?.id ?? null)
  const [historyOpen, setHistoryOpen] = useState(false)

  useEffect(() => {
    const loaded = loadHistory(storageKey)
    setItems(loaded)
    setActiveId(loaded[0]?.id ?? null)
    setHistoryOpen(false)
  }, [storageKey])

  const persist = useCallback(
    (next: ScriptAIOutputRecord[]) => {
      setItems(next)
      saveHistory(storageKey, next)
    },
    [storageKey],
  )

  const add = useCallback(
    (record: Omit<ScriptAIOutputRecord, 'id' | 'createdAt'>) => {
      const item: ScriptAIOutputRecord = {
        ...record,
        id: newId(),
        createdAt: Date.now(),
      }
      setItems((prev) => {
        const next = [item, ...prev].slice(0, MAX_ITEMS)
        saveHistory(storageKey, next)
        return next
      })
      setActiveId(item.id)
      return item
    },
    [storageKey],
  )

  const remove = useCallback(
    (id: string) => {
      setItems((prev) => {
        const next = prev.filter((it) => it.id !== id)
        saveHistory(storageKey, next)
        setActiveId((current) => (current === id ? next[0]?.id ?? null : current))
        return next
      })
    },
    [storageKey],
  )

  const clearAll = useCallback(() => {
    persist([])
    setActiveId(null)
  }, [persist])

  const clearDisplay = useCallback(() => {
    setActiveId(null)
  }, [])

  const active = useMemo(() => {
    if (!activeId) return null
    return items.find((it) => it.id === activeId) ?? null
  }, [activeId, items])

  return {
    items,
    active,
    activeId,
    setActiveId,
    historyOpen,
    setHistoryOpen,
    add,
    remove,
    clearAll,
    clearDisplay,
  }
}
